import { useEffect, useRef } from "react";

// Holds a screen wake lock while `active` is true so the device screen doesn't
// sleep mid-interview. Browsers silently drop the lock when the tab is hidden,
// so we re-acquire on visibilitychange. No-op where the API is unsupported.
export const useWakeLock = (active) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        // Cleanup may have already run while this request was in flight (e.g.
        // `active` flipped true→false before the browser granted the lock) — in
        // that case release immediately instead of stashing an orphaned sentinel
        // nothing will ever release.
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
      } catch {
        // Rejected (e.g. not visible, low battery) — nothing to recover; the
        // visibility handler will retry when the page next becomes visible.
      }
    };

    const handleVisibility = () => {
      if (!cancelled && document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (sentinelRef.current) {
        sentinelRef.current.release().catch(() => {});
        sentinelRef.current = null;
      }
    };
  }, [active]);
};

export default useWakeLock;
